'use client';

import type { UniversalSearchTabInfo, UniversalSearchTabItemInfo } from '@weberus/shared-types';
import { useEffect, useMemo, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { noop } from 'lodash-es';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { Tab } from '@yanolja-rnd/nol-design-system';
import { cn } from '@/app/_shared/utils/tw-cn';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';

interface VerticalTabProps {
  tabInfo?: UniversalSearchTabInfo['items'];
  selectedIndex?: number;
  localCheckIOCapacity?: {
    checkInDate: string;
    checkOutDate: string;
    capacityAdult: number;
    capacityChild: number;
    capacityChildAges: number[];
  };
  globalCheckIOCapacity?: {
    checkInDate: string;
    checkOutDate: string;
    capacityRooms: { adult: number; child: number[] }[];
  };
  serverLogMeta?: Record<string, unknown> | null;
  onClick?: (item: UniversalSearchTabItemInfo) => void;
  onImpression?: () => void;
  onOverflowChange?: (isOverflowing: boolean) => void;
}

export const NewBadge = () => (
  <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.5" width="12" height="12" rx="6" fill="#E31068" />
    <path
      d="M8.35937 3.67163V9.32788H7.34375L4.85938 5.73413H4.8125V9.32788H3.64062V3.67163H4.67188L7.14062 7.26538H7.19531V3.67163H8.35937Z"
      fill="white"
    />
  </svg>
);

export const VerticalTab = ({
  tabInfo = [],
  selectedIndex = 0,
  serverLogMeta,
  onClick,
  onImpression,
  onOverflowChange,
}: VerticalTabProps) => {
  const visitedTabsRef = useRef(new Set());

  useEffect(() => {
    // 첫 진입 카테고리는 BG 로그 전송하지 않도록 미리 추가
    if (tabInfo) {
      const selectedTab = tabInfo[selectedIndex];
      visitedTabsRef.current.add(selectedTab?.key);
    }
  }, []);

  useEffect(() => {
    if (!tabInfo || tabInfo.length === 0) {
      return;
    }

    // 이미 클릭한 탭은 BG 로그 전송하지 않음
    const selectedTab = tabInfo[selectedIndex];
    if (visitedTabsRef.current.has(selectedTab?.key)) {
      return;
    }

    // 방문한 탭에 추가하고 BG 로그 전송
    visitedTabsRef.current.add(selectedTab?.key);
    clientLogProtobuf(ProductListPageLog.SearchTabBtnName.bg, {
      logMeta: {
        ...serverLogMeta,
        ...selectedTab?.serverLogMeta,
      },
    })();
  }, [tabInfo]);

  const tabItems = useMemo(
    () =>
      tabInfo?.map((item) => ({
        label: `${item.title}${item.isNew ? '' : ''}`,
        extraElement: item.isNew ? (
          <span className="mb-9 ml-2">
            <NewBadge />
          </span>
        ) : null,
      })) || [],
    [tabInfo],
  );

  /**
   * 탭 클릭 핸들러
   * 탭 변경 시 URL 업데이트, 상태 변경, 스크롤 위치 복원 등을 처리
   */
  const handleClickTab = async (index: number) => {
    const item = tabInfo?.[index];

    // 선택한 탭이 이미 선택된 탭이거나 탭 정보가 없는 경우 처리 중단
    if (!item || selectedIndex === index) {
      return;
    }

    onClick?.(item);
  };

  const handleImpression = (inView: boolean) => {
    if (!inView) return;

    onImpression?.();
  };

  return (
    <InView className="relative" triggerOnce onChange={handleImpression}>
      <div className="flex h-65 w-full items-center">
        <Tab
          selectedIndex={selectedIndex}
          items={tabItems}
          shapeType="filled_capsule"
          slidesSpaceBetween={0}
          setSelectedIndex={noop}
          className="text-text-neutral-main"
          buttonClassName={cn(
            '!typography-body-14-bold !px-12 !min-h-40 bg-static-white text-text-neutral-main',
            '[&>span]:!inset-x-12 [&>span]:justify-start',
          )}
          onTabSelected={handleClickTab}
          onOverflowChange={(isOverflowing: boolean) => onOverflowChange?.(isOverflowing)}
        />
      </div>
      <div
        className="absolute right-0 top-0 z-[1] my-12 h-41 w-36"
        style={{
          background:
            'linear-gradient(90deg, rgba(255, 255, 255, 0) 20.83%, rgba(255, 255, 255, 0.7) 44.58%, #FFFFFF 92.08%)',
        }}
      />
    </InView>
  );
};
